import React, { useState, useCallback, useRef } from 'react';
import AccountStep from './steps/AccountStep';
import AboutMeStep from './steps/AboutMeStep';
import LocationStep from './steps/LocationStep';
import DancesStep from './steps/DancesStep';
import './SignupWizard.css';

const STEPS = [
  { id: 'account', label: 'Account', icon: 'bi-person' },
  { id: 'about', label: 'About Me', icon: 'bi-person-badge' },
  { id: 'location', label: 'Location', icon: 'bi-geo-alt' },
  { id: 'dances', label: 'Dances', icon: 'bi-music-note-beamed' },
];

const SignupWizard = ({ onComplete, onCancel }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    // Step 1: Account
    email: '',
    password: '',
    fullName: '',

    // Step 2: About Me
    dateOfBirth: '',
    gender: '',
    ethnicity: '',
    profilePicture: '',

    // Step 3: Location
    city: '',
    homeLocation: { city: '', country: '', formatted: '' },

    // Step 4: Dances
    dances: [],

    // Newsletter
    newsletterConsent: false,
  });

  // Use ref to track the latest accumulated data (fixes async state update issue)
  const accumulatedDataRef = useRef({});

  const [stepErrors, setStepErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateFormData = useCallback((updates) => {
    // Update both state and ref
    accumulatedDataRef.current = { ...accumulatedDataRef.current, ...updates };
    setFormData((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleNext = async (stepData) => {
    // Update form data with current step data
    updateFormData(stepData);

    if (currentStep < STEPS.length - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      // Final step - submit the form using the ref (always has latest data)
      const finalData = { ...accumulatedDataRef.current, ...stepData };
      console.log('📝 SignupWizard submitting data:', JSON.stringify(finalData, null, 2));
      await handleSubmit(finalData);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleSubmit = async (finalData) => {
    setIsSubmitting(true);

    try {
      // Call the onComplete callback with all form data
      await onComplete(finalData);
    } catch (error) {
      console.error('Signup error:', error);
      setStepErrors({ submit: error.message || 'Failed to create account. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    const commonProps = {
      formData,
      updateFormData,
      onNext: handleNext,
      onBack: handleBack,
      isFirstStep: currentStep === 0,
      isLastStep: currentStep === STEPS.length - 1,
      isSubmitting,
      submitError: stepErrors.submit,
    };

    switch (currentStep) {
      case 0:
        return <AccountStep {...commonProps} />;
      case 1:
        return <AboutMeStep {...commonProps} />;
      case 2:
        return <LocationStep {...commonProps} />;
      case 3:
        return <DancesStep {...commonProps} />;
      default:
        return null;
    }
  };

  return (
    <div className="signup-wizard">
      {/* Progress Indicator */}
      <div className="wizard-progress">
        {STEPS.map((step, index) => (
          <div
            key={step.id}
            className={`progress-step ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
          >
            <div className="step-indicator">
              {index < currentStep ? (
                <i className="bi bi-check-lg"></i>
              ) : (
                <i className={`bi ${step.icon}`}></i>
              )}
            </div>
            <span className="step-label">{step.label}</span>
            {index < STEPS.length - 1 && <div className="step-connector" />}
          </div>
        ))}
      </div>

      {/* Step Content */}
      <div className="wizard-content">
        {renderStep()}
      </div>

      {/* Cancel Link */}
      {onCancel && (
        <div className="wizard-footer">
          <button type="button" className="cancel-link" onClick={onCancel}>
            Already have an account? Sign in
          </button>
        </div>
      )}
    </div>
  );
};

export default SignupWizard;
